import { h, compile, defineAsyncComponent } from 'vue';
import {
    mapState, mapGetters, mapActions, mapMutations
} from 'vuex';
import { mapWritableState, mapState as piniaMapState } from 'pinia';
import moment from "moment";
import { store } from '@common/store/store';
import { sDs } from '@common/sDs/sDs';
import { library } from "@common/library/library";
import { controlStore, createStoreIndex } from '@common/control/controlStore';
import { errorHandler } from "@common/errorHandler/errorHandler";
import { configStore } from "@common/configStore";
import { recordStore } from "@common/recordStore";
import { ddStore, ddStoreFilter } from "@common/ddStore";
import { actionStore } from "@common/actionStore";
import { uStore } from "@common/userStore";
import baseComponentConfigs from "@components/devDash/data/baseComponentConfigs.js";
import { useVueToPrint } from "vue-to-print";

const AsyncFunction = (async () => {}).constructor;
const projectAllowsCaching = typeof VUE_PROJECT_NAME !== 'undefined' && VUE_PROJECT_NAME === 'devDash';
let freezedFunctions = {};
let excludeFunctionsFromFreeze = [
    'sortComparer',
    'rowDataBound',
    'affRowBoundFunc',
    'relRowBoundFunc',
    'getImagePosition',
    'onClick',
    'localUpdateFormField',
    'getSignerColor',
    'formFieldPropertiesChange',
    'createComp',
    'visibleItemCount'
];
/*
* global component config properties
* will be used in component editor
* */
const componentGlobalConfig = {
    cType: {
        type: "dropdown",
        label: "Component Type",
        required: true,
        disabled: true
    },
    cId: {
        type: "string",
        label: "Component ID",
        required: true,
    },
    cTitle: {
        type: "string",
        label: "Component Title",
        required: true,
    },
    cDescription: {
        type: "text",
        label: "Component Description",
    },
    isPublic: {
        type: "boolean",
        label: "Component Is Public",
    },
    isCore: {
        type: "boolean",
        label: "Core Component",
    },
    cTemplate: {
        type: "text",
        label: "Component Template",
    },
    showComponentActions: {
        type: "checkbox",
        label: "Show Component Actions (e.g. Reload or Open in new tab(not ready))"
    },
    showComponentActionsOnError: {
        type: "checkbox",
        label: "Show Component Actions on js/html error"
    }
};

/*
* global properties for components
* can be rewritten by setting up same properties directly in component
* */
const urlParsedComponents = [];
const maxParentDepth = 10;
let profileResizeTimeout;
const componentGlobalMixins = {
    data() {
        return {
            componentLoading: true,
            // componentReload: false,
            template: "",
            tmpStoreId: "",
            layoutStoreId: false,
            tmpLayoutDatamap: false,
            tmpDatamap: false,
            tmpParentStoreId: false,
            applyAutomationsCount: 0,
            tmpConfig: null,
            isComponent: true,
            refs: {},
            componentUnmounted: false,
            componentPendingSubscriptions: [], // @todo move to componentSubscriptions
            componentSubscriptions: {},
            isErrorCaptured: false
        };
    },
    props: {
        cId: {
            type: String
        },
        cType: {
            type: String
        },
        componentStoreId: {
        },
        staticStoreId: {
            default: false
        },
        uniqueStoreId: {
            default: false
        },
        editorEnabled: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        contextVolId: {
            type: Number
        },
        contextOppId: {
            type: Number
        },
        contextOrgId: {
            type: Number
        },
        contextLbeId: {
            type: Number
        },
        contextSchId: {
            type: Number
        },
        contextAeId: {
            type: Number
        },
        contextAffId: {
            type: Number
        },
        contextDialogId: {
            type: String
        },
        contextEntryPointId: {
            type: Number
        },
        contextDatasetId: {
            type: String
        },
        providedConfig: {},
        pendingSubscriptions: {}
    },
    computed: {
        user() {
            return uStore().activeUser;
        },
        userStore() {
            return uStore().getAllUsers;
        },
        ecUserProperty() {
            return uStore().getCoordinatorUser;
        },
        // ...piniaMapState(uStore, {
        //     user: 'activeUser',
        //     userStore: 'getAllUsers',
        //     ecUserProperty: 'getCoordinatorUser',
        // }),
        ...mapGetters({
            configStore: 'configStore/configStore'
        }),
        errorHandler() {
            let _this = this;
            return {
                log() {
                    let tmpArguments = [...arguments];
                    return errorHandler.log.apply(null, tmpArguments);
                },
                handle() {
                    let tmpArguments = [...arguments];
                    if (tmpArguments.length) {
                        tmpArguments[1] = typeof tmpArguments[1] === 'object' ? { ...tmpArguments[1], ...{ component: _this } } : tmpArguments[1] = { component: _this };
                    }
                    return errorHandler.handle.apply(null, tmpArguments);
                }
            };
        },
        sDs() {
            let _this = this;
            return {
                post() {
                    let sDsParams = {};
                    let tmpArguments = [...arguments];
                    for (let i = 0; i < tmpArguments.length; i++) {
                        if (i === tmpArguments.length - 1 && typeof tmpArguments[i] === 'object' && tmpArguments[i] !== null && tmpArguments[i].sDsParams) {
                            sDsParams = tmpArguments[i].sDsParams;
                            delete tmpArguments[i];
                        }
                    }
                    sDsParams.calledFromCId = _this.cId;
                    sDsParams.calledFromComponent = _this;
                    tmpArguments.push({ sDsParams: sDsParams });
                    if (typeof IS_JEST === 'undefined'
                        && _this.cId
                        && typeof errorHandler !== 'undefined'
                    ) {
                        errorHandler.addLastRequest(_this.cId, tmpArguments);
                    }
                    let results = sDs.post.apply(null, tmpArguments);
                    results.cId = _this.cId;
                    results.component = _this;
                    return results;
                }
            };
        },
        /* commandShouldExecuted() {
            let commandShouldExecuted = this.localStore ? this.localStore.get('executeCommand') : false;
            if (commandShouldExecuted) {
                library.eval.call(this, commandShouldExecuted);
            }
            return commandShouldExecuted;
        }, */
        ddStore() {
            // eslint-disable-next-line no-unused-expressions
            this.ddLoading;
            return ddStore.state.records[this.cId];
        },
        ddLoading() {
            return store.state.ddStore.loading;
        },
        recordStore() {
            return recordStore();
        },
        accountSettings() {
            if (!store.state.configStore['cfg'] || typeof store.state.configStore['cfg']['accountSettings'] === 'undefined') {
                configStore.getAccountSettings();
            }
            return store.state.configStore['cfg']['accountSettings'];
        },
        recruiterSettings() {
            if (!store.state.configStore['cfg'] || typeof store.state.configStore['cfg']['recruiterSettings'] === 'undefined') {
                configStore.getRecruiterSettings();
            }
            return store.state.configStore['cfg']['recruiterSettings'];
        },
        availableFields() {
            if (!store.state.configStore['cfg']) {
                let pspIds = [1];
                if (this.config.pspIds) {
                    pspIds = this.config.pspIds;
                }
                for (let i in pspIds) {
                    if (i === 'length') continue;
                    if (typeof store.state.configStore['cfg']['availableFields'] === 'undefined' || typeof store.state.configStore['cfg']['availableFields'][pspIds[i]] === 'undefined') {
                        configStore.getAvailableFieldList(pspIds[i]);
                    }
                }
            }
            return store.state.configStore['cfg']['availableFields'];
        },
        configReload() {
            let reload = this.localStore.get('configReload');
            if (reload) this.reloadConfig();
            return reload;
        },
        config: {
            get() {
                if (this.providedConfig) return this.providedConfig;
                return this.tmpConfig;
            },
            set(value) {
                if (typeof this.localStore !== 'undefined') {
                    this.localStore.set('config', value);
                    this.tmpConfig = value;
                }
            }
        },
        dataTestId() {
            let prefix = '';
            if (typeof this.uniqueStoreId === 'string') prefix = this.uniqueStoreId + "_";
            else if (this.fromPreview) prefix = 'preview_';
            return prefix + this.cId;
        },
        fromPreview: function () {
            let fromPreview = false;
            let parent = this.$parent;
            for (let i = 0; i < maxParentDepth; i++) {
                if (!parent) break;
                if (parent && (parent.fromPreview || parent.cType === 'componentPreview')) {
                    fromPreview = true;
                    break;
                }
                parent = parent.$parent;
            }
            return fromPreview;
        },
        storeId: function () {
            if (!this.tmpStoreId) {
                this.tmpStoreId = this.cId;
                let uniqueId = this.uniqueStoreId === true ? library.getUniqueID() : this.uniqueStoreId;
                if (uniqueId && (this.uniqueStoreId || (!this.staticStoreId && !this.layoutId))) {
                    this.tmpStoreId += "__" + uniqueId;
                }
            }
            if (this.layoutId) {
                return [this.layoutId, this.tmpStoreId];
            }
            return this.tmpStoreId;
        },
        layoutId: function () {
            let layoutId = null;
            let parent = this.$parent;
            for (let i = 0; i < maxParentDepth; i++) {
                if (!parent) break;
                if (parent && (parent.layoutStoreName || parent.layoutStoreId)) {
                    layoutId = parent.layoutStoreName || parent.layoutStoreId;
                    if (!this.layoutStoreId) {
                        this.layoutStoreId = layoutId;
                    }
                    break;
                }
                parent = parent.$parent;
            }
            return layoutId;
        },
        parentStoreId: function () {
            if (!this.tmpParentStoreId) {
                let parent = this.$parent;
                for (let i = 0; i < maxParentDepth; i++) {
                    if (!parent) break;
                    if (parent && parent.isParent) {
                        this.tmpParentStoreId = parent.storeName;
                        break;
                    }
                    if (parent && parent.parentStore) {
                        this.tmpParentStoreId = parent.parentStore;
                        break;
                    }
                    parent = parent.$parent;
                }
                if (!this.tmpParentStoreId) this.tmpParentStoreId = this.layoutId;
            }
            return this.tmpParentStoreId;
        },
        volId() {
            if (this.contextVolId) return this.contextVolId;
            if (this.layoutId) return this.$store.state[this.layoutId].volId;
            return 0;
        },
        oppId() {
            if (this.contextOppId) return this.contextOppId;
            if (this.layoutId) return this.$store.state[this.layoutId].oppId;
            return 0;
        },
        orgId() {
            if (this.contextOrgId) return this.contextOrgId;
            if (this.layoutId) return this.$store.state[this.layoutId].orgId;
            return 0;
        },
        lbeId() {
            if (this.contextLbeId) return this.contextLbeId;
            if (this.layoutId) return this.$store.state[this.layoutId].lbeId;
            return 0;
        },
        schId() {
            if (this.contextSchId) return this.contextSchId;
            if (this.layoutId) return this.$store.state[this.layoutId].schId;
            return 0;
        },
        aeId() {
            if (this.contextAeId) return this.contextAeId;
            if (this.layoutId) return this.$store.state[this.layoutId].aeId;
            return 0;
        },
        entryPointId() {
            if (this.contextEntryPointId) return this.contextEntryPointId;
            if (this.layoutId) return this.$store.state[this.layoutId].entryPointId;
            return 0;
        },
        affId() {
            if (this.contextAffId) return this.contextAffId;
            if (this.layoutId) return this.$store.state[this.layoutId].affId;
            return 0;
        },
        dialogId() {
            if (this.contextDialogId) return this.contextDialogId;
            if (this.layoutId) return this.$store.state[this.layoutId].dialogId;
            return 0;
        },
        datasetId() {
            if (this.contextDatasetId) return this.contextDatasetId;
            if (this.layoutId) return this.$store.state[this.layoutId].datasetId;
            return 0;
        },
        dialogName() {
            return this.contextDialogName || false;
        },
        componentStyles() {
            let output = {};
            let conversions = {
                borderRadius: {
                    '1': '0',
                    '2': '3px',
                    '3': '5px',
                    '4': '10px',
                }
            };
            let parentStyles = {};
            if (this.$parent && this.$parent.$parent && this.$parent.$parent.cType === 'layoutContainer' && this.$parent.$parent.childComponentStyles) {
                parentStyles = this.$parent.$parent.childComponentStyles;
            }
            let styles;
            if (!this.config.useCommonComponentStyles) {
                styles = library.mergeObj({}, parentStyles, this.config.commonComponentStyles);
            } else styles = parentStyles;
            for (let key in styles) {
                if (key === 'length') continue;
                let newKey = key.replace('component', '');
                newKey = newKey[0].toLowerCase() + newKey.substring(1);
                output[newKey] = conversions[newKey] ? conversions[newKey][styles[key]] : styles[key];
            }
            return output;
        },
        preInitControls() {
            // eslint-disable-next-line no-use-before-define
            let componentsInitialConfig = componentStore.get('componentsConfig');
            let preInitControls = !!(this.cType && componentsInitialConfig[this.cType] && componentsInitialConfig[this.cType].preInitControls);
            if (typeof IS_JEST !== 'undefined' && IS_JEST && !componentsInitialConfig[this.cType].preInitJestTest) {
                preInitControls = true;
            }
            return preInitControls;
        },
        layoutDatamap: {
            get() {
                if (this.tmpLayoutDatamap) return this.tmpLayoutDatamap;
                if (this.config && typeof this.config.layoutDatamap !== 'undefined') {
                    this.tmpLayoutDatamap = this.config.layoutDatamap;
                }
                let parent = this.$parent;
                for (let i = 0; i < maxParentDepth; i++) {
                    if (!parent) break;
                    if (parent && parent.layoutDatamap) {
                        if (this.tmpLayoutDatamap) {
                            this.tmpLayoutDatamap = [...this.tmpLayoutDatamap, ...parent.layoutDatamap];
                        } else {
                            this.tmpLayoutDatamap = parent.layoutDatamap;
                        }
                        break;
                    }
                    parent = parent.$parent;
                }
                return this.tmpLayoutDatamap || [];
            },
            set(value) {
                this.tmpLayoutDatamap = value;
            }
        },
        datamap: {
            get() {
                if (!this.tmpDatamap) {
                    this.tmpDatamap = [];
                    for (let i in this.layoutDatamap) {
                        if (i === 'length') continue;
                        if (this.layoutDatamap[i].from === this.cId || this.layoutDatamap[i].to === this.cId) {
                            this.tmpDatamap.push(this.layoutDatamap[i]);
                        }
                    }
                }
                return this.tmpDatamap;
            },
            set(value) {
                this.tmpDatamap = value;
            }
        },
        isFromSettingsEditor() {
            let isFromSettingsEditor = false;
            let parent = this.$parent;
            while (parent) {
                if (parent?.cType === 'settingsEditor') {
                    isFromSettingsEditor = true;
                    break;
                }
                parent = parent.$parent;
            }
            return isFromSettingsEditor;
        },
        currentInstance() {
            return configStore.getCurrentInstanceDetails(true);
        },
        showComponentActions() {
            return this.config && (this.config.showComponentActions || (this.config.showComponentActionsOnError && this.isErrorCaptured));
        }
    },
    methods: {
        openPrintView() {
            // TODO move this to a global place for use in layoutContainer component
            let printLoadingComplete = () => { library.hideLoadingIndicator('body'); };
            let printProps = {
                content: this.$refs.printRef,
                bodyClass: 'print-view',
                onBeforeGetContent: this.beforePrintFunction,
                onBeforePrint: printLoadingComplete,
                onAfterPrint: this.afterPrintFunction,
                pageStyle: this?.config?.commonPrintStyles
            };
            const { handlePrint } = useVueToPrint(printProps);
            // showLoadingIndicator: /* istanbul ignore next */ function (objId, hideAfterSec, message, addClass)
            library.showLoadingIndicator('body', false, `<div class="print-loading-message"><p><i class="fa-regular fa-print-magnifying-glass"></i>Print view generating... please wait.</p></div>`);
            handlePrint();
        },
        errorCaptured(tmpError) {
            if (this.componentUnmounted) return false;
            this.isErrorCaptured = true;
            if (typeof this.localErrorCaptured === 'function') this.localErrorCaptured(tmpError);
            if (typeof errorHandler !== 'undefined') {
                errorHandler.handle(tmpError, { component: this });
            }
            return false;
        },
        showLoadingIndicator() {
            library.showLoadingIndicator(this.cId + '_componentWrapper');
        },
        hideLoadingIndicator() {
            library.hideLoadingIndicator(this.cId + '_componentWrapper');
        },
        ddStoreFilter(value, cId) {
            if (this.ddLoading) {
                return '';
            }
            if (!cId && this.cId) cId = this.cId;
            return ddStoreFilter(value, cId);
        },
        updateLockedControls() {
            if (!this.isFromSettingsEditor) return;
            for (let i in this.controls) {
                if (i === 'length') continue;
                if (this.controls[i].name === 'LOCK_SETTINGS') continue;
                if (this.controls[i].disabled !== this.disabled) {
                    this.controls[i].disabled = this.disabled;
                }
            }
        },
        async reloadConfig() {
            this.config = null;
            this.localStore.set('configReload', false);
            // eslint-disable-next-line no-use-before-define
            let refresh = this.fromPreview;
            // eslint-disable-next-line no-use-before-define
            this.config = await componentStore.getConfig(this.cId, this.cType, refresh);
        },
        async applyTestIds() {
            // eslint-disable-next-line no-use-before-define
            let componentsInitialConfig = componentStore.get('componentsConfig');
            // Sets element attributes
            let setElements = (elements, componentData, suffix, testIds, i) => {
                let setComponentData = (tmpData, j) => {
                    for (let k = 1; k < componentData.length; k++) {
                        let index = componentData[k] === '_index_' ? j : componentData[k];
                        tmpData = tmpData[index] ? tmpData[index] : null;
                        if (!tmpData) break;
                    }
                    return tmpData;
                };
                for (let j in elements) {
                    if (j === 'length') continue;
                    // Skip if the element doesn't have the property or if data-test-id is already set
                    if (!elements.hasOwnProperty(j) || elements[j].getAttribute('data-test-id')) continue;
                    let testId = this.dataTestId;
                    let data = componentData.length ? this[componentData[0]] : null;
                    if (data) {
                        data = setComponentData(data, j);
                        if (typeof data === 'string') {
                            testId += '_' + data.toLowerCase().replace(/[ :-]/g, "_");
                        }
                    }
                    if (suffix) {
                        testId += '_' + suffix;
                    }
                    elements[j].setAttribute('data-test-id', testId);
                    // TODO get this into settingsEditor some how
                    testIds[i].generatedId = testId;
                    // Force Commit
                }
            };
            if (this.cType && componentsInitialConfig[this.cType] && componentsInitialConfig[this.cType].dataTestIds) {
                let testIds = componentsInitialConfig[this.cType].dataTestIds;
                for (let i in testIds) {
                    if (i === 'length') continue;
                    let elements = [];
                    let querySelector = testIds[i].querySelector;
                    if (!querySelector) continue;
                    let suffix = testIds[i].suffix;
                    let componentData = testIds[i].cData ? testIds[i].cData.split('.') : [];

                    // Target the element using querySelector like: '#id1' or '#id1 .a-class .another.another2'
                    // eslint-disable-next-line no-underscore-dangle
                    elements = this.$el.querySelectorAll(querySelector);

                    // search the element for 5 sec
                    let maxAutomationsIds = 10;
                    if ((!elements || !elements.length) && this.applyAutomationsCount <= maxAutomationsIds) {
                        this.applyAutomationsCount += 1;
                        setTimeout(this.applyTestIds, 500); // NOSONAR
                    } else if (elements && elements.length) {
                        setElements(elements, componentData, suffix, testIds, i);
                    }
                }
            }
        },
        applyDataFormatter() {
            if (this.config && this.config.dataFormatter) {
                for (let i in this.config.dataFormatter) {
                    if (i === 'length') continue;
                    let value = library.getByPath(this, this.config.dataFormatter[i].from);
                    let prevValue = library.getByPath(this, this.config.dataFormatter[i].to);
                    if (typeof value !== 'undefined' && !library.isEqualObj(value, prevValue)) {
                        library.setByPath(this, this.config.dataFormatter[i].to, value);
                    }
                }
            }
        },
        updatePropsToVuex(property, value, force) {
            if (this.componentLoading) return;
            this.applyDataFormatter();
            // let notUpdateProps = [];// ['cId', 'cType'];
            let setStoreProp = (prevValue, tmpValue) => {
                if (!library.isEqualObj(prevValue, tmpValue)) {
                    this.localStore.set(property, tmpValue);
                    if (typeof this.onPropsUpdate !== 'undefined') {
                        this.onPropsUpdate(property);
                    }
                } else if (force && typeof this.onPropsUpdate !== 'undefined') {
                    this.onPropsUpdate(property);
                }
            };
            if (
                typeof this.$props === 'object'
                && typeof this.localStore !== 'undefined'
                && typeof this.localStore.set !== 'undefined'
                // && notUpdateProps.indexOf(property) === -1
            ) {
                // store all props in vuex
                if (typeof property !== 'undefined') {
                    let tmpValue = value || this.$props[property];
                    let prevValue = this.localStore.get(property);
                    setStoreProp(prevValue, tmpValue);
                } else {
                    for (let i in this.$props) {
                        if (i === 'length') continue;
                        this.localStore.set(i, this.$props[i]);
                    }
                }
            }
            if (!property && typeof this.onPropsUpdate !== 'undefined') {
                this.onPropsUpdate(property);
            }
        },
        controlValue(name) {
            return controlStore().getValue(name, this.storeId);
        },
        controlItems(name) {
            let controlStoreIndex = createStoreIndex(this.storeId, name);
            return controlStore()[controlStoreIndex].items;
        },
        completeLoad() {
            // for the correct validation in legacy ProfileTemplate.jsinc we should set vue-component-loaded=true for the all vueBridge components
            if (
                typeof this?.$el?.parentNode?.getAttribute === 'function'
                && this.$el.parentNode.getAttribute('vue-component-mounted') === 'true'
            ) {
                this.$el.parentNode.setAttribute('vue-component-loaded', 'true');
            }
        },
        watchSubscription(subscriptionID, options) {
            /*
            * options:
            * watchProperty initialLoading, loading (default) etc...
            * collectAs
            * startFnc
            * endFnc
            * isMultiple
            * */
            if (!options) options = {};
            let watchProperty = options.watchProperty || 'loading';
            let collectAs = options.collectAs || watchProperty;
            if (!this.componentSubscriptions[collectAs]) this.componentSubscriptions[collectAs] = [];
            if (watchProperty === 'initialLoading' && this.recordStore.subscriptions?.[subscriptionID]?.initialLoading) {
                // Only add subscription if initialLoading is true
                if (this.componentSubscriptions[collectAs].indexOf(subscriptionID) === -1) {
                    this.componentSubscriptions[collectAs] = [...this.componentSubscriptions[collectAs], ...[subscriptionID]];
                }
                if (typeof options.startFnc === 'function') options.startFnc(subscriptionID);
            }
            let stopWatch = this.$watch('recordStore.subscriptions.' + subscriptionID + '.' + watchProperty, (subLoading) => {
                if (!subLoading) {
                    if (!options.isMultiple) stopWatch();
                    let tmpComponentSubscriptions = library.cloneObj(this.componentSubscriptions[collectAs]);
                    const subIndex = tmpComponentSubscriptions.indexOf(subscriptionID);
                    if (subIndex > -1) tmpComponentSubscriptions.splice(subIndex, 1);
                    this.componentSubscriptions[collectAs] = library.cloneObj(tmpComponentSubscriptions);
                    if (typeof options.endFnc === 'function') options.endFnc(subscriptionID);
                } else if (watchProperty !== 'initialLoading') {
                    if (this.componentSubscriptions[collectAs].indexOf(subscriptionID) === -1) {
                        this.componentSubscriptions[collectAs] = [...this.componentSubscriptions[collectAs], ...[subscriptionID]];
                    }
                    if (typeof options.startFnc === 'function') options.startFnc(subscriptionID);
                }
            });
        },
        reloadComponent() {
            this.componentLoading = true;
            setTimeout(() => {
                this.isErrorCaptured = false;
                this.componentLoading = false;
                if (typeof this.onLoad === "function") {
                    this.onLoad();
                }
            }, 1000);
        }
    },
    watch: {
        'disabled': function (prevValue, newValue) {
            if (!library.isEqualObj(prevValue, newValue)) {
                this.updateLockedControls(false, true);
            }
        },
        'componentPendingSubscriptions': function (newValue, prevValue) {
            if (!library.isEqualObj(prevValue, newValue)) {
                this.$emit('update:pendingSubscriptions', newValue);
            }
        }
    },
    components: {
        'control': defineAsyncComponent(() => import('@common/control/control.vue')),
        'loading': defineAsyncComponent(() => import('@common/loading/loading.vue')),
        'freezed': defineAsyncComponent(() => import('@common/freezed/freezed.vue')),
        'labelWrapper': defineAsyncComponent(() => import('@common/control/labelWrapper')),
        'udfDetailsPanel': defineAsyncComponent(() => import('@common/udfDetailsPanel.vue')),
        'settingsCog': defineAsyncComponent(() => import('@common/settingsEditor/settingsEditorCog.vue')),
        'ejs-button': defineAsyncComponent(() => import('@common/syncfusion/button')),
    },
    /* istanbul ignore next */
    render() {
        if (!this.template) {
            return h("loading", "");
        }
        if (!this.freezed && typeof this.template !== 'function') {
            // const result = compile(this.template);
            this.template = compile(this.template);
            // this.$options.staticRenderFns = result.staticRenderFns;
        } else if (this.freezed) {
            this.template = compile('<freezed problem-with-component="' + this.cId + '"></freezed>');
        }
        if (typeof this.onRender !== 'undefined') {
            this.onRender();
        }
        return h(this.template(this));
    },
    updated() {
        if (this.componentLoading) return;
        this.applyDataFormatter();
        if (typeof this.onUpdate !== 'undefined') {
            this.onUpdate();
        }
        library.logicParser.call(this);
        this.applyTestIds();
        // eslint-disable-next-line camelcase
        /* if (typeof lib_resizeToContent === 'function' && this.$el && this.$el.offsetParent !== null) {
            if (profileResizeTimeout) clearTimeout(profileResizeTimeout);
            profileResizeTimeout = setTimeout(() => {
                // eslint-disable-next-line camelcase
                lib_resizeToContent(1);
            }, 500);
        } */
        // this.updateLockedControls(false, true);
    },
    created() {
        if (typeof IS_JEST === 'undefined') {
            uStore().getUserData();
        }
        if (typeof this.onCreate !== 'undefined') {
            this.onCreate();
        }
        if (typeof window.vueRecordStore === 'undefined' && typeof IS_JEST === 'undefined') {
            window.vueRecordStore = {};
            window.vueRecordStore.renewSubscriptions = recordStore().renewSubscriptions;
            if (library.optimization.storeSubscriptionsGlobaly) window.vueRecordStore.subscriptions = recordStore().subscriptions;
        }
    },
    /* istanbul ignore next */
    mounted() {
        let initialLoader = document.getElementById('initialLoader');
        if (initialLoader) {
            initialLoader.style.display = 'none';
        }
        this.componentLoading = true;
        if (this.vuex && typeof this.localStore === 'undefined') {
            this.localStore = store.registerModule(this.storeId, library.cloneObj(this.vuex));
        }
        // if (typeof this.onPropsUpdate === 'function'
        //     && this.cType === 'grid'
        //     && typeof VUE_PROJECT_NAME !== 'undefined'
        //     && VUE_PROJECT_NAME === 'devDash'
        // ) {
        //     let storeName = Array.isArray(this.storeId) ? this.storeId.join('.') : this.storeId;
        //     for (let i in this.$props) {
        //         if (i.indexOf('prop') === -1) continue;
        //         let propPath = '$store.state.' + storeName + '.' + i;
        //         this.$watch(propPath, (function (propName) {
        //             return function (newValue, oldValue) {
        //                 if (!library.isEqualObj(newValue, oldValue)) {
        //                     if (this) this.updatePropsToVuex(propName, newValue, true);
        //                 }
        //             };
        //         }(i)), { deep: true });
        //     }
        // }
        let setControlValues = (paramControls) => {
            for (let name in paramControls) {
                if (name === 'length') continue;
                controlStore().setValue(name, this.storeId, paramControls[name], this.parentStoreId);
            }
        };
        let setValuesFromUrlParams = async () => {
            if (!uStore().anyLoggedUser) return;
            let urlParams = await library.urlParser.parse(this.cId);
            if (urlParams && !library.objIsEmpty(urlParams) && !urlParsedComponents[this.cId]) {
                urlParsedComponents[this.cId] = true;
                for (let i in urlParams) {
                    if (i === 'length') continue;
                    if (typeof this[i] === 'undefined') continue;
                    if (i === 'controls') {
                        this['quickLinkControlsValues'] = library.cloneObj(urlParams['controls']);
                        setControlValues(urlParams['controls']);
                    } else {
                        this[i] = urlParams[i];
                    }
                }
            }
            if (typeof errorHandler !== 'undefined' && this.cId === errorHandler.get('repeatComponentID')) {
                let lastActions = errorHandler.get('lastActions');
                if (lastActions && lastActions.length) {
                    let tmpLastAction = lastActions[lastActions.length - 1];
                    if (typeof this[tmpLastAction.fName] === 'function') {
                        setTimeout(() => {
                            this[tmpLastAction.fName].apply(null, tmpLastAction.fParams);
                        }, 3000);
                    }
                }
            }
        };
        // eslint-disable-next-line no-use-before-define
        let componentsInitialConfig = componentStore.get('componentsConfig');
        let layoutId = typeof this.parentStoreId === 'string' ? this.parentStoreId : this.layoutStoreId;
        if (this.cType && componentsInitialConfig[this.cType] && componentsInitialConfig[this.cType].globalMethods) {
            if (typeof actionStore.state.globalMethods[this.cType] === 'undefined') actionStore.state.globalMethods[this.cType] = {};
            if (typeof actionStore.state.globalMethods[this.cType][layoutId] === 'undefined') actionStore.state.globalMethods[this.cType][layoutId] = {};
            for (let i in componentsInitialConfig[this.cType].globalMethods) {
                if (i === 'length') continue;
                if (typeof this[componentsInitialConfig[this.cType].globalMethods[i]] === 'function') {
                    actionStore.state.globalMethods[this.cType][layoutId][componentsInitialConfig[this.cType].globalMethods[i]] = this[componentsInitialConfig[this.cType].globalMethods[i]];
                }
            }
        }
        let refresh = this.fromPreview;
        // eslint-disable-next-line no-use-before-define
        componentStore.getConfig(this.cId, this.cType, refresh).then((config) => {
            this.config = config;
            // Set stylesheets for component
            if (this.cId !== 'labelWrapper' && this.config && this.config.componentCssStylesheet) {
                this.configStore.componentStylesheets[this.cId] = new CSSStyleSheet();
                this.configStore.componentStylesheets[this.cId]
                    .replace(`[cid="${this.cId}"] { & ${this.config.componentCssStylesheet} }`);
                let stylesheets = Object.values(this.configStore.componentStylesheets);
                document.adoptedStyleSheets = [...document.adoptedStyleSheets, ...stylesheets];
            }
            if (this.config && Array.isArray(this.config.controls) && !this.controlsAdded) {
                this.config.controls.forEach((control) => {
                    if (control.initOnRender) return; // continue
                    let controlStoreIndex = createStoreIndex(this.storeId, control.name);
                    controlStore().addControl(control, this, controlStoreIndex);
                });
                this.controlsAdded = true;
            }
            // TODO can we remove customControls?
            if (config.customControls && Array.isArray(config.customControls)) {
                config.customControls.forEach((control) => {
                    let controlStoreIndex = createStoreIndex(this.storeId, control.name);
                    controlStore().addControl(control, this, controlStoreIndex);
                    if (this.controlConfigs && !this.controlConfigs[control.name]) {
                        this.controlConfigs[control.name] = control;
                    }
                });
            }
            if (typeof config.cTemplate !== 'undefined') {
                let template = config.cType === 'labelWrapper'
                    ? '<div v-if="!componentLoading" :cId="cId" :storeId="storeId" :data-test-id="dataTestId" class="label-wrapper">'
                    : '<div v-if="!componentLoading" :cId="cId" :storeId="storeId" ref="printRef" :data-test-id="dataTestId" :id="cId + \'_componentWrapper\'" class="component-wrapper" :class="{\'force-show-component-actions-wrapper\': showComponentActions && isErrorCaptured}">'
                        + `<div v-if="showComponentActions" class="component-actions-wrapper">
                            <ejs-button iconCss="e-icons e-repeat" @click="reloadComponent"></ejs-button>
                            <!--future actions open in new tab/maximize/minimize etc.-->
                        </div>
                        <div v-if="showComponentActions && isErrorCaptured" class="component-error-wrapper">Sorry, there's a problem with this component. Our developers have been notified. Please try to reload component.</div>
                        <button v-if="config?.enablePrinting && (typeof initialLoadComplete === 'undefined' || initialLoadComplete)" type="button" @click="openPrintView" class="global-print-button"><i class="fa-solid fa-print"></i>Print</button>`;
                template = template
                    + (
                        config.cType !== 'settingsEditor' && config.cType !== 'labelWrapper'
                            ? '<div v-if="editorEnabled"><settings-cog v-if="editorEnabled" :unsaved-config="config && config.unsaved" :component-id="cId" :preview-id="storeId" :editor-enabled="editorEnabled" :test-mode="testMode"></settings-cog></div>'
                            : ''
                    )
                    /* + (this.fromPreview ? '<div v-show="false">{{commandShouldExecuted}}</div>' : '') */
                    + config.cTemplate
                    + '</div>';
                this.template = compile(template);
            }
            this.componentLoading = false;
            library.logicParser.call(this);
            this.updatePropsToVuex();
            this.tmpLayoutDatamap = null;
            this.tmpDatamap = null;
            this.$nextTick(async () => {
                this.$emit('componentMounted', this.cId);
                await setValuesFromUrlParams();
                if (typeof this.onLoad === "function") {
                    this.onLoad();
                }
                let cTypesWithCustomCompleteLoad = ['pdf'];
                if (cTypesWithCustomCompleteLoad.indexOf(this.cType) === -1) this.completeLoad();
            });
        });
    },
    errorCaptured(tmpError) {
        return this.errorCaptured(tmpError);
    },
    unmounted() {
        this.componentUnmounted = true;
        if (this && this.config && this.config.cType !== 'labelWrapper') {
            store.unregisterModule(this.storeId);
        }
        if (this && this.recordStore) {
            this.recordStore.unsubscribe(false, this.cId);
        }
        if (this && this.ddStore) {
            ddStore.removeUnusedItems(this.cId);
        }
        // eslint-disable-next-line no-use-before-define
        let componentsInitialConfig = componentStore.get('componentsConfig');
        if (this.cType
            && componentsInitialConfig[this.cType]
            && componentsInitialConfig[this.cType].globalMethods
            && actionStore
            && actionStore.state.globalMethods
            && actionStore.state.globalMethods[this.cType]
            && actionStore.state.globalMethods[this.cType][this.layoutStoreId]
        ) {
            for (let i in componentsInitialConfig[this.cType].globalMethods) {
                if (i === 'length') continue;
                delete actionStore.state.globalMethods[this.cType][this.layoutStoreId][componentsInitialConfig[this.cType].globalMethods[i]];
            }
        }
    },
    beforeCreate() {
        if (!this.$options.controlConfigs) return;
        let getLayoutId = function (_this) {
            let layoutId = null;
            let parent = _this.$parent;
            for (let i = 0; i < maxParentDepth; i++) {
                if (!parent) break;
                if (parent && (parent.layoutStoreName || parent.layoutStoreId)) {
                    layoutId = parent.layoutStoreName || parent.layoutStoreId;
                    if (!_this.layoutStoreId) {
                        _this.layoutStoreId = layoutId;
                    }
                    break;
                }
                parent = parent.$parent;
            }
            return layoutId;
        };
        let getStoreId = function (_this) {
            let storeId = _this.cId;
            let layoutId = getLayoutId(_this);
            let uniqueId = _this.uniqueStoreId === true ? library.getUniqueID() : _this.uniqueStoreId;
            if (uniqueId && (_this.uniqueStoreId || (!_this.staticStoreId && !_this.layoutId))) {
                storeId += "__" + uniqueId;
            }
            if (layoutId) {
                return [layoutId, storeId];
            }
            return [storeId];
        };
        let storeId = getStoreId(this);
        let controlStoreMappings = {};
        this.controlConfigs = library.cloneObj(this.$options.controlConfigs);
        Object.values(this.$options.controlConfigs)
            .forEach((control) => {
                let keyMapper = {};
                let controlStoreIndex = createStoreIndex(storeId, control.name);
                keyMapper[controlStoreIndex] = controlStoreIndex;
                controlStoreMappings = {
                    ...controlStoreMappings,
                    ...mapWritableState(controlStore, keyMapper),
                };
            });
        this.$options.computed = {
            ...this.$options.computed,
            ...controlStoreMappings
        };
    }
};
const templateComponentMixins = {
    data() {
        return {
            tmpStoreId: "",
            layoutStoreId: false,
            tmpParentStoreId: false,
        };
    },
    computed: {
        // ...piniaMapState(uStore, {
        //     user: 'activeUser',
        //     userStore: 'getAllUsers',
        //     ecUserProperty: 'getCoordinatorUser',
        // }),
        user() {
            return uStore().activeUser;
        },
        userStore() {
            return uStore().getAllUsers;
        },
        ecUserProperty() {
            return uStore().getCoordinatorUser;
        },
        ...mapGetters({
            configStore: 'configStore/configStore'
        }),
        recordStore() {
            return recordStore();
        },
        accountSettings() {
            if (!store.state.configStore['cfg'] || typeof store.state.configStore['cfg']['accountSettings'] === 'undefined') {
                configStore.getAccountSettings();
            }
            return store.state.configStore['cfg']['accountSettings'];
        },
        recruiterSettings() {
            if (!store.state.configStore['cfg'] || typeof store.state.configStore['cfg']['recruiterSettings'] === 'undefined') {
                configStore.getRecruiterSettings();
            }
            return store.state.configStore['cfg']['recruiterSettings'];
        },
        storeId: function () {
            if (!this.tmpStoreId) {
                this.tmpStoreId = this.cId;
                let uniqueId = this.uniqueStoreId === true ? library.getUniqueID() : this.uniqueStoreId;
                if (uniqueId && (this.uniqueStoreId || (!this.staticStoreId && !this.layoutId))) {
                    this.tmpStoreId += "__" + uniqueId;
                }
            }
            if (this.layoutId) {
                return [this.layoutId, this.tmpStoreId];
            }
            return this.tmpStoreId;
        },
        layoutId: function () {
            let layoutId = null;
            let parent = this.$parent;
            for (let i = 0; i < maxParentDepth; i++) {
                if (!parent) break;
                if (parent && (parent.layoutStoreName || parent.layoutStoreId)) {
                    layoutId = parent.layoutStoreName || parent.layoutStoreId;
                    if (!this.layoutStoreId) {
                        this.layoutStoreId = layoutId;
                    }
                    break;
                }
                parent = parent.$parent;
            }
            return layoutId;
        },
        parentStoreId: function () {
            if (!this.tmpParentStoreId) {
                let parent = this.$parent;
                for (let i = 0; i < maxParentDepth; i++) {
                    if (!parent) break;
                    if (parent && parent.isParent) {
                        this.tmpParentStoreId = parent.storeName;
                        break;
                    }
                    if (parent && parent.parentStore) {
                        this.tmpParentStoreId = parent.parentStore;
                        break;
                    }
                    parent = parent.$parent;
                }
                if (!this.tmpParentStoreId) this.tmpParentStoreId = this.layoutId;
            }
            return this.tmpParentStoreId;
        },
        volId() {
            return this.layoutId ? this.$store.state[this.layoutId].volId : 0;
        },
        oppId() {
            return this.layoutId ? this.$store.state[this.layoutId].oppId : 0;
        },
        orgId() {
            return this.layoutId ? this.$store.state[this.layoutId].orgId : 0;
        },
        aeId() {
            return this.layoutId ? this.$store.state[this.layoutId].aeId : 0;
        },
        entryPointId() {
            return this.layoutId ? this.$store.state[this.layoutId].entryPointId : 0;
        },
        lbeId() {
            return this.layoutId ? this.$store.state[this.layoutId].lbeId : 0;
        },
        datasetId() {
            return this.layoutId ? this.$store.state[this.layoutId].datasetId : 0;
        }
    }
};

const locals = {
    stripcIdsubId(cId) {
        if (typeof cId === 'string' && cId.indexOf('__') !== -1) {
            let tmpcId = cId.split('__');
            if (tmpcId.length && tmpcId[0].length) {
                cId = tmpcId[0];
            }
        }
        return cId;
    },
    setPropNameInStore(propertyName, i, value) {
        if (this.datamap[i].to === 'global' || this.datamap[i].to === 'parentStore' || this.datamap[i].to === 'thisStore') {
            if (this.datamap[i].to === 'global') {
                store.state[propertyName] = value;
            }
            if (this.datamap[i].to === 'thisStore' && store.state[this.layoutStoreId]) {
                store.state[this.layoutStoreId][propertyName] = value;
            }
            if (this.datamap[i].to === 'parentStore' && store.state[this.parentStoreId]) {
                store.state[this.parentStoreId][propertyName] = value;
            }
        } else {
            if (store.state[this.layoutStoreId] && store.state[this.layoutStoreId][this.datamap[i].to]) {
                store.state[this.layoutStoreId][this.datamap[i].to][propertyName] = value; // NOSONAR
            }
        }
    },
    setStoreStateValues(storeId, layoutId, property, value) {
        if (storeId && layoutId && store.state[layoutId] && store.state[layoutId][storeId]) {
            store.state[layoutId][storeId][property] = value; // NOSONAR
        }
        if (storeId && store.state[storeId]) {
            store.state[storeId][property] = value; // NOSONAR
        }
    },
    getDatamapValue(i) {
        let value;
        if (typeof this.datamap[i].value !== 'undefined') {
            value = this.datamap[i].value;
        } else if (this.datamap[i].from === 'global' || this.datamap[i].from === 'parentStore' || this.datamap[i].from === 'thisStore') {
            if (this.datamap[i].from === 'global') {
                if (typeof store.state[this.datamap[i].property] === 'undefined') store.state[this.datamap[i].property] = {};
                value = store.state[this.datamap[i].property];
            }
            if (this.datamap[i].from === 'thisStore' && store.state[this.layoutStoreId]) {
                if (typeof store.state[this.layoutStoreId][this.datamap[i].property] === 'undefined') store.state[this.layoutStoreId][this.datamap[i].property] = {};
                value = store.state[this.layoutStoreId][this.datamap[i].property];
            }
            if (this.datamap[i].from === 'parentStore' && store.state[this.parentStoreId]) {
                if (typeof store.state[this.parentStoreId][this.datamap[i].property] === 'undefined') store.state[this.parentStoreId][this.datamap[i].property] = {};
                value = store.state[this.parentStoreId][this.datamap[i].property];
            }
        } else {
            if (store.state[this.layoutStoreId] && store.state[this.layoutStoreId][this.datamap[i].from]) {
                if (typeof store.state[this.layoutStoreId][this.datamap[i].from] === 'undefined') store.state[this.layoutStoreId][this.datamap[i].from] = {};
                value = store.state[this.layoutStoreId][this.datamap[i].from][this.datamap[i].property];
            }
        }
        return value;
    },
    setValueFromLocalStore(prevValue, value, property) {
        if (this.localStore && typeof value !== 'undefined' && !library.isEqualObj(this.localStore.get(property), value)) {
            this.localStore.set(property, value);
        }
        if (this.localStore) {
            value = this.localStore.get(property);
        }
        if (this && typeof this.onMappedDataUpdate === 'function' && !library.isEqualObj(prevValue, value)) {
            this.onMappedDataUpdate(property);
        }
        return value;
    },
    setAdditionalData(record, additionalData) {
        let addlData = record.additionalData || additionalData;
        if (addlData) {
            let inceptionDate = moment('2020-10-01').format();
            let lastModify = addlData.cLastModify || inceptionDate;
            let createdDate = addlData.cCreatedDate || inceptionDate;
            record.cCreatedBy = addlData.cCreatedBy || 'system';
            record.cCreatedDate = createdDate;
            record.cLastModify = lastModify;
            record.cModifiedBy = addlData.cModifiedBy || '';
            record.cLastModifyUnixDay = moment(lastModify)
                .startOf('day').format('x');
            record.dateLastModified = moment(lastModify).format('MM-DD-YYYY hh:mma');
            record.daysSinceLastModify = moment()
                .diff(moment(lastModify), 'days');
            // Adjust for partial days
            if (record.daysSinceLastModify !== 0) record.daysSinceLastModify++;
        }
    }
};

/*
* vuex module for components.
* get from sDs and store components config
* */
const componentStore = {
    namespaced: true,
    state: {
        components: {},
        pendingConfigRequests: {},
        componentsConfig: {},
        componentsTypes: [],
        componentPaths: [],
        componentsNames: {},
        canBeUsedAsItemsSource: [
            'getList',
            'getTypes',
            'getComponentPaths'
        ],
        componentsFetched: false,
        statisticProps: [
            'outOfDate',
            'additionalData',
            'cCreatedBy',
            'cCreatedDate',
            'cLastModify',
            'cLastModifyUnixDay',
            'cModifiedBy\n',
            'count_loads',
            'dateLastModified',
            'daysSinceLastModify',
            'formattedLastLoad',
            'instance',
            'isUnused',
            'lastLoad',
            'last_load',
            'modifiedInstances',
            'statistics',
            'usedInInstances'
        ]
    },
    getters: {
        // get all registered components
        getComponents: function (state) {
            return function () {
                return state.components;
            };
        },
        getComponentOptions(state) {
            return state.components;
        },
        // get property of component. cType for example
        getProperty: function (state) {
            return function (payload) {
                let [cId, property] = Object.values(payload);
                let result;
                cId = locals.stripcIdsubId(cId);
                if (!state.components[cId]) {
                    cId = state.componentsNames[cId];
                }
                if (property === "all") {
                    result = state.components[cId];
                } else {
                    result = state.components[cId][property];
                }
                return result;
            };
        },
        // load list of components from sDs
        /* istanbul ignore next */
        getList(state) {
            return async function (payload) {
                let [onlyShown, force] = Object.values(payload);
                let storedComponents = [];
                let tmpComponents = {};
                if (!force && state.componentsFetched) {
                    tmpComponents = library.cloneObj(state.components);
                } else {
                    let sDsParams = {
                        sDsParams: {
                            force: true
                        }
                    };
                    componentStore.mutate('componentsFetched', true);
                    let response = {};
                    let storeKey = library.getSessionStorage('devDashEnv') + '/sds_getComponentList';

                    if (!force && projectAllowsCaching) {
                        let isUpdated = await sDs.compareDataAge(storeKey);
                        if (!isUpdated) response = await library.cachedTransaction('get', storeKey);
                    }
                    if (response.status !== 'success') {
                        response = await sDs.post("sds_getComponentList", sDsParams);
                    }
                    if (response.status === 'success') {
                        await library.removeCache(library.getSessionStorage('devDashEnv') + '/sds_getComponentConfig', true);

                        // Process Statistics and merge it with the component in response from sds_getComponentList
                        let statItems = await componentStore.getStatistics([]);
                        let statistics = {};
                        statItems.forEach((iStatItem) => {
                            if (iStatItem.last_load) {
                                let lastLoad = iStatItem.last_load.split('-');
                                lastLoad = lastLoad[1] + '-' + lastLoad[2] + '-' + lastLoad[0]; // NOSONAR
                                iStatItem.lastLoad = Number(moment(lastLoad).format('x'));
                                iStatItem.formattedLastLoad = moment(iStatItem.lastLoad).format('MM/DD/YYYY');
                            }
                            let existingStatItem = statistics[iStatItem.cId];
                            if (existingStatItem) {
                                // Handle existing
                                if (iStatItem.instance && existingStatItem.instances.indexOf(iStatItem.instance) === -1) {
                                    existingStatItem.instances.push(iStatItem.instance);
                                }
                                if (iStatItem.lastLoad && iStatItem.lastLoad > existingStatItem.lastLoad) {
                                    existingStatItem.lastLoad = iStatItem.lastLoad;
                                    existingStatItem.formattedLastLoad = moment(iStatItem.lastLoad).format('MM/DD/YYYY');
                                }
                            }
                            else {
                                iStatItem.instances = [];
                                if (iStatItem.instance) iStatItem.instances.push(iStatItem.instance);
                                statistics[iStatItem.cId] = iStatItem;
                            }
                        });
                        // merge statistics with the rest of the list
                        if (projectAllowsCaching && !response.fromCaching) await library.cachedTransaction('put', storeKey, JSON.stringify(response.data));
                        storedComponents = response.data.map((component) => ({ ...component, ...statistics[component.cId], statistics: statistics[component.cId] }));
                    }
                    let componentList = [
                        ...baseComponentConfigs,
                        ...storedComponents
                    ];
                    for (let i in componentList) {
                        if (i === 'length') continue;
                        // Format component data for UI
                        componentList[i].fullId = componentList[i].cType + ':::' + componentList[i].cId;
                        locals.setAdditionalData(componentList[i]);
                        let cId = componentList[i].cId;
                        tmpComponents[cId] = componentList[i];
                    }
                    tmpComponents = library.mergeObj({}, state.components, tmpComponents);
                    componentStore.mutate('components', tmpComponents);
                }
                for (let i in tmpComponents) {
                    if (typeof tmpComponents[i].isUnused === 'undefined') tmpComponents[i].isUnused = false;
                }
                tmpComponents = library.cloneObj(tmpComponents);
                if (onlyShown) {
                    for (let cId in tmpComponents) {
                        if (cId === 'length') continue;
                        if (cId.indexOf('__') !== -1 || tmpComponents[cId].showInCmpList === false) {
                            delete tmpComponents[cId];
                        }
                    }
                }
                return tmpComponents;
            };
        },
        setDefaultConfig(state) {
            return function (payload) {
                let [settings] = Object.values(payload);
                for (let i in state.components[settings.cType]) {
                    if (i === 'length' || i === 'instances' || state.statisticProps.indexOf(i) !== -1) continue;
                    if (typeof settings[i] === 'undefined') {
                        if (state.componentsConfig[settings.cType] && state.componentsConfig[settings.cType].editorSettings && state.componentsConfig[settings.cType].editorSettings[i] && typeof state.componentsConfig[settings.cType].editorSettings[i].default !== 'undefined') {
                            settings[i] = library.cloneObj(state.componentsConfig[settings.cType].editorSettings[i].default);
                        } else {
                            settings[i] = state.components[settings.cType][i];
                        }
                    }
                }
                // get controls from component.vue
                if (settings.cType !== 'buttonPanel' && settings.cType !== 'controlPanel' && state.components[settings.cType]) {
                    settings.controls = library.cloneObj(state.components[settings.cType].controls);
                } else if (settings.cType === 'controlPanel') {
                    let processItemConfig = (control) => {
                        if (control.itemConfigObj && Array.isArray(control.itemConfigObj.itemKeys)) {
                            let keysArr = library.cloneObj(control.itemConfigObj.itemKeys);
                            control.itemConfig = library.cloneObj(control.itemConfigObj);
                            delete control.itemConfig.itemKeys;
                            control.itemConfig.keys = {};
                            control.itemConfig.newItem = {};
                            control.default = [];
                            keysArr.forEach((item) => {
                                // Create the key
                                let key = item.itemProperty;
                                // Set default
                                delete item.itemProperty;
                                // map new item
                                control.itemConfig.newItem[key] = item.default;
                                // Map control properties
                                control.itemConfig.keys[key] = item;
                            });
                        }
                    };
                    for (let i in settings.controls) {
                        if (i === 'length') continue;
                        let control = settings.controls[i];
                        if (control.type === 'array') {
                            processItemConfig(control);
                            control.default = [];
                        }
                    }
                }
                return library.cloneObj(settings);
            };
        },
        // get component settings from sDs and store in state
        getConfig(state) {
            return async function (payload) {
                let [cId, cType, refresh] = Object.values(payload);
                let settings;
                if (!cId && !cType) return {};
                if (state.componentsNames[cId]) {
                    cType = state.componentsNames[cId];
                }
                let pendingResponse = state.pendingConfigRequests[cId];
                if (pendingResponse) await pendingResponse;
                if (state.components[cId]?.loaded) {
                    settings = library.cloneObj(state.components[cId]);
                    for (let i in state.components[cType]) {
                        if (i === 'length') continue;
                        if (typeof settings[i] === 'undefined') {
                            settings[i] = library.cloneObj(state.components[cType][i]);
                        }
                    }
                } else {
                    let realcId = cId;
                    cId = locals.stripcIdsubId(cId);
                    let storeKey = library.getSessionStorage('devDashEnv') + '/sds_getComponentConfig/' + cId;
                    // Prevent multiple requests for the same component before first one completes
                    let pendingConfigResolve;
                    state.pendingConfigRequests[cId] = new Promise((resolve) => {
                        pendingConfigResolve = resolve;
                    });
                    let response = {};
                    let isBase = false;
                    let isCore = cId === cType;
                    if (cId && !isCore) {
                        if (baseComponentConfigs.find((comp) => comp.cId === cId)) {
                            let comp = baseComponentConfigs.find((iComp) => iComp.cId === cId);
                            settings = comp.cConfig || comp;
                            settings.cType = comp.cType;
                            isBase = true;
                        } else {
                            if (!refresh && projectAllowsCaching) {
                                let isUpdated = await sDs.compareDataAge(storeKey);
                                if (!isUpdated) response = await library.cachedTransaction('get', storeKey);
                            }
                            if (response.status !== 'success') {
                                let sDsParams = {
                                    sDsParams: {
                                        combine: true
                                    }
                                };
                                response = await sDs.post("sds_getComponentConfig", cId, sDsParams);
                                response = library.cloneObj(response);
                                if (response.status === 'success') {
                                    if (response.data[cId]) {
                                        response.data = response.data[cId];
                                    } else {
                                        response.status = 'error';
                                    }
                                }
                            }
                        }
                    }
                    if (response.status === 'success' && !library.objIsEmpty(response.data)) {
                        if (projectAllowsCaching && !response.fromCaching) await library.cachedTransaction('put', storeKey, JSON.stringify(response.data));
                        settings = response.data;
                        // if (settings.cConfig) settings = settings.cConfig;
                    } else if (!isBase) {
                        settings = library.cloneObj(state.components[cId]);
                    }
                    if (!settings) {
                        settings = library.cloneObj(state.components[cType]);
                    }
                    if (!settings) {
                        return {};
                    }
                    // if (typeof cType === 'undefined' && settings.cType) cType = settings.cType;
                    // set up default config properties, that were not loaded or not stored in file
                    settings = componentStore.setDefaultConfig(settings);
                    for (let i in componentGlobalConfig) {
                        if (i === 'length') continue;
                        if (typeof settings[i] === 'undefined') {
                            settings[i] = '';
                        }
                    }
                    if (typeof settings['fromDb'] === 'undefined') {
                        settings['fromDb'] = false;
                    }
                    locals.setAdditionalData(settings);
                    settings.cId = realcId;
                    settings.isCore = isCore;
                    pendingConfigResolve();
                    componentStore.mutateComponent(realcId, settings);
                    state.components[realcId].loaded = true;
                }
                // Adjust Template based on config
                let compConfig = state.componentsConfig[settings.cType];
                settings.cTemplate = settings.customTemplate || (compConfig && compConfig.cTemplate);
                // @todo remove on 1st March 2021
                if (!Array.isArray(settings.controls)) {
                    let tmpControls = [];
                    for (let i in settings.controls) {
                        if (i === 'length') continue;
                        if (typeof settings.controls[i].name === 'undefined') {
                            settings.controls[i].name = i;
                        }
                        tmpControls.push(settings.controls[i]);
                    }
                    settings.controls = tmpControls;
                }
                return settings;
            };
        },
        getStatistics() {
            return async function (payload) {
                let [cId] = Object.values(payload);
                let statistics = [];
                let response = await sDs.post("sds_getComponentStatistics", cId);
                if (response.status === 'success') {
                    statistics = response.data;
                }
                return statistics;
            };
        },
        get(state) {
            return function (payload) {
                // TODO Fix this - see accordionMenu.vue 'componentStore/get'
                let [property1, property2] = Object.values(payload);
                if (property2) return state[property1][property2];
                return state[property1];
            };
        },
        getTestDataKeys() {
            return async function (payload) {
                let [cId, refresh] = Object.values(payload);
                let testData = [];
                let response;
                let storeKey = library.getSessionStorage('devDashEnv') + '/sds_getTestDataKeys';
                if (!refresh && projectAllowsCaching) {
                    let isUpdated = await sDs.compareDataAge('sds_getTestDataKeys');
                    if (!isUpdated) response = await library.cachedTransaction('get', storeKey);
                }
                if (!response || response.status !== 'success') {
                    response = await sDs.post("sds_getTestDataKeys", cId);
                }
                if (response && response.status === 'success') {
                    if (projectAllowsCaching && !response.fromCaching) await library.cachedTransaction('put', storeKey, JSON.stringify(response.data));
                    testData = response.data;
                }
                return testData;
            };
        },
        getTestData() {
            return async function (payload) {
                let [key] = Object.values(payload);
                let testData = [];
                let response = await sDs.post("sds_getTestData", key);
                if (response.status === 'success') {
                    testData = response.data;
                }
                return testData;
            };
        },
        getModuleMixins() {
            return function (payload) {
                let [component, module, options] = Object.values(payload);
                if (!component.name || !module) return {};
                if (!options) options = {};
                let defaultOptions = {
                    basedOnComponentId: true,
                    mapState: true,
                    mapGetters: true,
                    mapActions: true,
                    mapMutations: true
                };

                let mergedOptions = {
                    ...defaultOptions,
                    ...options
                };

                let tmpGetters = {};
                let tmpStates = {};
                let tmpActions = {};
                let tmpMutations = {};
                let moduleMixins;

                let mapMergedOptions = (tmpState) => {
                    if (component.props && component.props[tmpState]) return;
                    if (mergedOptions.mapState || (Array.isArray(mergedOptions.mapState) && mergedOptions.mapGetters.indexOf(tmpState) !== -1)) {
                        tmpStates[tmpState] = (function (cType, iState) {
                            return /* istanbul ignore next */ function () {
                                let result;
                                if (typeof store.state[component.name] !== 'undefined' && typeof store.state[component.name][iState] !== "undefined") {
                                    // if component based om componentId, this.stateName will return state[stateName][cId] from vuex module
                                    if (typeof this.cId !== 'undefined') {
                                        result = store.state[cType][iState][this.cId];
                                    } else {
                                        result = store.state[cType][iState];
                                    }
                                    // result = state[cType][iState];
                                }
                                return result;
                            };
                        }(component.name, tmpState));
                    }
                };

                // add default this.config getter to component
                if (module.getters && !module.getters.config) {
                    tmpGetters['config'] = 'componentStore/getConfig';
                }

                // add default this.controlValue action to component
                if (component.name !== 'control') {
                    tmpActions['controlValue'] = (function (gName) {
                        return /* istanbul ignore next */function () {
                            let args = [...arguments];
                            args.shift();
                            args[args.length] = this.cId;
                            args = { ...args };
                            return store.getters[gName](args);
                        };
                    }('control/getValue'));
                }

                // mapping getters to component. each mapped getter will available in component through this.getterName
                if (mergedOptions.mapGetters) {
                    for (let tmpGetter in module.getters) {
                        if (tmpGetter === 'length') continue;
                        if (mergedOptions.mapGetters || (Array.isArray(mergedOptions.mapGetters) && mergedOptions.mapGetters.indexOf(getter) !== -1)) {
                            tmpGetters[tmpGetter] = component.name + '/' + tmpGetter;
                        }
                    }
                }

                // mapping states to component. each mapped state will be available in component through this.stateName
                if (mergedOptions.mapState) {
                    for (let tmpState in module.state) {
                        if (tmpState === 'length') continue;
                        mapMergedOptions(tmpState);
                    }
                }

                // mapping actions to component. each mapped action will available in component through this.actionName()
                if (mergedOptions.mapActions) {
                    for (let tmpAction in module.actions) {
                        if (tmpAction === 'length') continue;
                        if (mergedOptions.mapActions || (Array.isArray(mergedOptions.mapActions) && mergedOptions.mapActions.indexOf(tmpAction) !== -1)) {
                            tmpActions[tmpAction] = (function (aName) {
                                return /* istanbul ignore next */ function () {
                                    let args = [...arguments];
                                    args.shift();
                                    // cId will auto added to action. You can call action from component without cId
                                    args[args.length] = this.cId;
                                    args = { ...args };
                                    return store.dispatch(aName, args, { root: true });
                                };
                            }(component.name + '/' + tmpAction));
                        }
                    }
                }

                // mapping mutations to component. each mapped mutation will available in component through this.mutationName()
                if (mergedOptions.mapMutations) {
                    for (let tmpMutation in module.mutations) {
                        if (tmpMutation === 'length') continue;
                        if (mergedOptions.mapMutations || (Array.isArray(mergedOptions.mapMutations) && mergedOptions.mapMutations.indexOf(tmpMutation) !== -1)) {
                            tmpMutations[tmpMutation] = (function (mName) {
                                return /* istanbul ignore next */ function () {
                                    let args = [...arguments];
                                    args.shift();
                                    // cId will auto added to mutation. You can call mutation from component without cId
                                    args[args.length] = this.cId;
                                    args = { ...args };
                                    return store.commit(mName, args, { root: true });
                                };
                            }(component.name + '/' + tmpMutation));
                        }
                    }
                }

                moduleMixins = {
                    computed: {
                        ...mapState(tmpStates),
                        ...mapGetters(tmpGetters)
                    },
                    methods: {
                        ...mapActions(tmpActions),
                        ...mapMutations(tmpMutations)
                    },
                };

                return moduleMixins;
            };
        },
        getComponentMixins() {
            return function (payload) {
                let [options] = Object.values(payload);
                if (!options) options = {};
                if (options.useTemplateComponentMixins) return library.cloneObj(templateComponentMixins);
                let componentMixins = library.cloneObj(componentGlobalMixins);
                let defaultOptions = {
                    addComponents: true,
                    addMount: true,
                    addDefaultProps: true
                };
                const mergedOptions = {
                    ...defaultOptions,
                    ...options
                };

                if (!mergedOptions.addComponents) {
                    delete componentMixins.components;
                }
                if (!mergedOptions.addMount) {
                    delete componentMixins.mounted;
                }
                if (!mergedOptions.addDefaultProps) {
                    delete componentMixins.props.cId;
                    delete componentMixins.props.cType;
                }

                return componentMixins;
            };
        },

        getTypes(state) {
            return function () {
                return state.componentsTypes.sort((a, b) => a.localeCompare(b));
            };
        },
        getComponentPaths(state) {
            return function () {
                return state.componentPaths;
            };
        }
    },
    mutations: {
        mutateComponent: function (state, payload) {
            let [cId, settings, previewId, component, path] = Object.values(payload);
            let getCoreSettings = (component) => {
                for (let i in component) {
                    if (i === 'length') continue;
                    if (typeof settings[i] === 'undefined') {
                        settings[i] = component[i];
                    }
                }
            };
            if (cId && settings) {
                for (let i in settings) {
                    if (i === 'length') continue;
                    if (typeof settings[i] !== 'undefined' && settings[i] !== null && settings[i].type) {
                        settings[i] = settings[i].value ? settings[i].value : settings[i].default;
                    }
                }
                let tmpComponents = state.components;
                let tmpComponentsNames = state.componentsNames;
                let tmpComponentsTypes = state.componentsTypes;
                let tmpComponentPaths = state.componentPaths;
                let cType = parseInt(cId, 10) ? false : cId;
                if (typeof (tmpComponents[cId]) === "undefined") {
                    tmpComponents[cId] = {};
                }
                tmpComponentsNames[cId] = settings.cType;
                if (settings.cId) {
                    tmpComponentsNames[settings.cId] = settings.cType;
                }
                if (cType) {
                    // get core component settings of not defined in current config
                    getCoreSettings(state.components[cType]);
                }
                if (settings.cType && tmpComponentsTypes.indexOf(settings.cType) === -1) {
                    tmpComponentsTypes.push(settings.cType);
                }
                if (path) {
                    if (component.name) {
                        let pathOption = {
                            text: component.name,
                            value: path,
                            htmlTag: library.kebabCase(component.name),
                            path: path
                        };
                        tmpComponentPaths.push(pathOption);
                    }
                }

                const existingComponentData = tmpComponents[cId] || {};
                tmpComponents[cId] = { ...existingComponentData, ...settings };

                state.components = {};
                state.components = tmpComponents;
                state.componentsNames = {};
                state.componentsNames = tmpComponentsNames;
                state.componentsTypes = {};
                state.componentsTypes = tmpComponentsTypes;
                state.componentPaths = {};
                state.componentPaths = tmpComponentPaths;
                let mutation;
                if (previewId) mutation = previewId.join('/') + '/set';
                else {
                    let mutationId = '/' + cId + '__devdash_test_window/set';
                    for (let mKey in store._mutations) {
                        if (mKey === 'length') continue;
                        if (mKey.indexOf(mutationId) !== -1) {
                            mutation = mKey;
                        }
                    }
                }
                if (mutation) {
                    if (typeof store._mutations[mutation] !== 'undefined') {
                        store.commit(mutation, ['config', settings]);
                        store.commit(mutation, ['configReload', true]);
                    }
                }
            }
        },
        // The 'mutate' function remains unchanged
        mutate: function (state, payload) {
            let [property, value] = Object.values(payload);
            state[property] = value;
        },
    },
    actions: {
        initComponent({ state, commit }, payload) {
            let [component, settings, path] = Object.values(payload);
            let cId = null;
            let vuex = {
                namespaced: true,
                state: {
                    config: null,
                    configReload: false,
                    // executeCommand: false
                }
            };
            // Getter and Setter for computed properties on a component
            // This puts data into the store when a computed property is used
            let generateCompComputedSetterGetter = (iProp) => ({
                set: (function (property) {
                    return async function (value) {
                        let start = performance.now();
                        this.localStore.set(property, value);
                        this.localStore.set('prop' + library.capitalizeFirstLetter(property), value);
                        // Sets datamap properties
                        let setDatamapProps = () => {
                            for (let i in this.datamap) { // NOSONAR
                                if (i === 'length') continue;
                                if (
                                    this.datamap[i].property !== property
                                    || this.datamap[i].from !== this.cId) {
                                    continue;
                                }
                                let propertyName = this.datamap[i].as || property;
                                locals.setPropNameInStore.call(this, propertyName, i, value);
                            }
                        };
                        // Set Store State
                        let setStoreData = async () => {
                            let storeId = null;
                            let layoutId = null;
                            if (!this.componentStoreId) return;
                            storeId = Array.isArray(this.componentStoreId) ? this.componentStoreId[1] : this.componentStoreId;
                            layoutId = Array.isArray(this.componentStoreId) ? this.componentStoreId[0] : null;
                            locals.setStoreStateValues.call(this, storeId, layoutId, property, value);
                            let config = await componentStore.getConfig(storeId);
                            if (store.state[config.cType]) {
                                let tmpValue = library.cloneObj(store.state[config.cType][property]);
                                if (typeof tmpValue === 'undefined') tmpValue = {};
                                if (typeof (tmpValue[storeId]) === "undefined") {
                                    tmpValue[storeId] = {};
                                }
                                tmpValue[storeId] = value;
                                store.state[config.cType][property] = {};
                                store.state[config.cType][property] = tmpValue; // NOSONAR
                            }
                        };
                        if (this.datamap && this.datamap.length) {
                            setDatamapProps();
                        } else {
                            await setStoreData();
                        }
                        if (library.optimization.log.props) {
                            // eslint-disable-next-line no-console
                            console.log(this.cId, 'set', property, performance.now() - start);
                        }
                    };
                }(iProp)),
                get: (function (property) {
                    return function () {
                        let start = performance.now();
                        let value;
                        // get previous value
                        let prevValue = null;
                        if (this.localStore) {
                            prevValue = this.localStore.get(property);
                        }
                        // set datamap prop
                        let setDatamapProp = (i) => {
                            if (
                                this.datamap[i].to !== this.cId
                                || (this.datamap[i].as !== property && this.datamap[i].property !== property)
                            ) {
                                if (this['prop' + library.capitalizeFirstLetter(property)]) {
                                    value = this.localStore.get('prop' + library.capitalizeFirstLetter(property));
                                }
                            } else {
                                value = locals.getDatamapValue.call(this, i);
                            }
                        };
                        let inDatamapKey = (key, prop) => {
                            if (!this.datamap || !this.datamap.length) return false;
                            return this.datamap
                                .filter((mapEntry) => mapEntry[key] === prop)
                                .length > 0;
                        };
                        // get the value from the correct store
                        if (inDatamapKey('as', property)) {
                            for (let i in this.datamap) { // NOSONAR
                                if (i === 'length') continue;
                                setDatamapProp(i);
                            }
                        } else if (this['prop' + library.capitalizeFirstLetter(property)]) {
                            // TODO figure out why this.localStore is not setup for tabList when watchers are processed
                            value = this.localStore
                                ? this.localStore.get('prop' + library.capitalizeFirstLetter(property))
                                : this['prop' + library.capitalizeFirstLetter(property)];
                        }
                        else value = locals.setValueFromLocalStore.call(this, prevValue, value, property);
                        if (typeof this.onMappedDataUpdate === 'function' && !library.isEqualObj(prevValue, value)) {
                            this.onMappedDataUpdate(property);
                        }
                        if (library.optimization.log.props) {
                            // eslint-disable-next-line no-console
                            console.log(this.cId, 'get', property, performance.now() - start);
                        }
                        return value;

                        // if (this['prop' + library.capitalizeFirstLetter(property)]) {
                        //     value = this.localStore.get('prop' + library.capitalizeFirstLetter(property));
                        // } else if (this.datamap && this.datamap.length) {
                        //     // TODO can we remove this with changes above?
                        //     for (let i in this.datamap) { // NOSONAR
                        //         if (i === 'length') continue;
                        //         setDatamapProp(i);
                        //     }
                        // }
                        // value = locals.setValueFromLocalStore.call(this, prevValue, value, property);
                        // return value;
                    };
                }(iProp))
            });
            let getComponentConfig = () => {
                let tmp = componentStore.get('componentsConfig');
                // set default template in the component for access from getConfig
                component.cTemplate = settings.cTemplate;
                tmp[component.name] = library.cloneObj(component);
                state.componentsConfig = {};
                state.componentsConfig = tmp;
                if (typeof component.props === 'undefined') component.props = {};
                component.props.cId = {
                    default: component.name
                };
                component.props.cType = {
                    default: component.name
                };
            };

            // Set Props
            let setProps = (props, data) => {
                if (cId === 'labelWrapper') {
                    component.mixins.push(componentStore.getComponentMixins());
                } else {
                    for (let i in props) {
                        if (props[i].excludeFromStore) continue;
                        if (i === 'length') continue;
                        // Exclude componentGlobalMixins props from being converted to computed data
                        if (typeof componentGlobalMixins.props[i] !== 'undefined') continue;
                        if (typeof vuex.state[i] === 'undefined') {
                            vuex.state[i] = typeof props[i].default !== 'undefined' ? props[i].default : null;
                        }
                        if (typeof component.computed === 'undefined') {
                            component.computed = {};
                        }
                        component.computed[i] = generateCompComputedSetterGetter(i);
                        if (typeof component.props === 'undefined') component.props = {};
                        if (typeof component.props['prop' + library.capitalizeFirstLetter(i)] === 'undefined') {
                            component.props['prop' + library.capitalizeFirstLetter(i)] = { default: library.cloneObj(props[i].default) };
                        }
                        delete props[i];
                        delete component.props[i];
                    }
                    if (typeof data.controls === 'undefined') data.controls = {};
                    component.mixins.push(componentStore.getComponentMixins());
                    // Create computed
                    for (let i in data) {
                        if (i === 'length') continue;
                        if (typeof vuex.state[i] === 'undefined') {
                            vuex.state[i] = data[i];
                        }
                        if (typeof component.computed === 'undefined') {
                            component.computed = {};
                        }
                        component.computed[i] = {
                            // eslint-disable-next-line no-loop-func
                            set: (function (property) {
                                return async function (value) {
                                    let start = performance.now();
                                    let prevValue = this.localStore.get(property);
                                    if (property !== 'initialLoadComplete') {
                                        if (typeof freezedFunctions[this.cId + '_set_' + property] === 'undefined') freezedFunctions[this.cId + '_set_' + property] = 0;
                                        freezedFunctions[this.cId + '_set_' + property]++;
                                        if (freezedFunctions[this.cId + '_set_' + property] > 1000) {
                                            if (!library.isEqualObj(prevValue, value) && !this.freezed) {
                                                this.freezed = true;
                                                setTimeout(() => {
                                                    errorHandler.handle("Browser freeze!!! Caused by '" + this.cId + "' component by setting '" + property + "' property.");
                                                }, 1000);
                                            }
                                            return;
                                        }
                                        setTimeout(() => {
                                            delete freezedFunctions[this.cId + '_set_' + property];
                                        }, 1000);
                                    }
                                    let setTmpValue = (storeId) => {
                                        let tmpValue = library.cloneObj(store.state[config.cType][property]);
                                        if (typeof tmpValue === 'undefined') tmpValue = {};
                                        if (typeof (tmpValue[storeId]) === "undefined") {
                                            tmpValue[storeId] = {};
                                        }
                                        tmpValue[storeId] = value;
                                        return tmpValue;
                                    };
                                    let setStoreState = async () => {
                                        let storeId = Array.isArray(this.componentStoreId) ? this.componentStoreId[1] : this.componentStoreId;
                                        let layoutId = Array.isArray(this.componentStoreId) ? this.componentStoreId[0] : null;
                                        locals.setStoreStateValues.call(this, storeId, layoutId, property, value);
                                        let config = await componentStore.getConfig(storeId);
                                        if (store.state[config.cType]) {
                                            let tmpValue = setTmpValue(storeId);
                                            store.state[config.cType][property] = {};
                                            store.state[config.cType][property] = tmpValue; // NOSONAR
                                        }
                                    };
                                    this.localStore.set(property, value);
                                    this.localStore.set('data' + library.capitalizeFirstLetter(property), value);
                                    if (this.datamap && this.datamap.length) {
                                        for (let i in this.datamap) { // NOSONAR
                                            if (i === 'length') continue;
                                            if (this.datamap[i].property === property && this.datamap[i].from === this.cId) {
                                                let propertyName = this.datamap[i].as || property;
                                                locals.setPropNameInStore.call(this, propertyName, i, value);
                                            }
                                        }
                                    } else if (this.componentStoreId) {
                                        await setStoreState();
                                    }
                                    if (typeof this.onUpdate === 'function' && typeof IS_JEST === 'undefined' && !library.isEqualObj(prevValue, value)) this.onUpdate();
                                    if (library.optimization.log.computed) {
                                        // eslint-disable-next-line no-console
                                        console.log(this.cId, 'set', property, performance.now() - start);
                                    }
                                };
                            }(i)),
                            get: (function (property) {
                                return function () {
                                    let start = performance.now();
                                    let value;
                                    let prevValue = null;
                                    if (this.localStore) {
                                        prevValue = this.localStore.get(property);
                                    }
                                    if (this['data' + library.capitalizeFirstLetter(property)]) {
                                        value = this.localStore.get('data' + library.capitalizeFirstLetter(property));
                                    } else if (this.datamap && this.datamap.length) {
                                        for (let i in this.datamap) { // NOSONAR
                                            if (i === 'length') continue;
                                            if ((this.datamap[i].as === property || this.datamap[i].property === property) && this.datamap[i].to === this.cId) {
                                                value = locals.getDatamapValue.call(this, i);
                                            }
                                        }
                                    }
                                    value = locals.setValueFromLocalStore.call(this, prevValue, value, property);
                                    if (library.optimization.log.computed) {
                                        // eslint-disable-next-line no-console
                                        console.log(this.cId, 'get', property, performance.now() - start);
                                    }
                                    return value;
                                };
                            }(i))
                        };
                        delete data[i];
                    }
                    for (let i in component.props) {
                        if (i === 'length') continue;
                        if (typeof vuex.state[i] === 'undefined') {
                            vuex.state[i] = null;
                        }
                        if (typeof component.watch === 'undefined') component.watch = {};
                        if (typeof component.watch[i] !== 'undefined') continue;
                        component.watch[i] = (function (property) {
                            return function () {
                                if (this) this.updatePropsToVuex(property);
                            };
                        }(i));
                    }
                    // Create methods
                    for (let i in component.methods) {
                        if (i === 'length') continue;
                        component.methods['method' + library.capitalizeFirstLetter(i)] = component.methods[i];
                        // eslint-disable-next-line no-loop-func
                        if (component.methods[i] instanceof AsyncFunction) {
                            component.methods[i] = (function (fName) {
                                return function () {
                                    if (typeof this !== 'undefined') {
                                        if (typeof freezedFunctions[this.cId + '_' + fName] === 'undefined') freezedFunctions[this.cId + '_' + fName] = 0;
                                        freezedFunctions[this.cId + '_' + fName]++;
                                        if (freezedFunctions[this.cId + '_' + fName] > 1000 && !this.freezed && excludeFunctionsFromFreeze.indexOf(fName) === -1) {
                                            this.freezed = true;
                                            setTimeout(() => {
                                                errorHandler.handle("Browser freeze!!! Caused by '" + this.cId + "' component in '" + fName + "' function.");
                                            }, 1000);
                                            return;
                                        }
                                        setTimeout(() => {
                                            delete freezedFunctions[this.cId + '_' + fName];
                                        }, 1000);
                                        if (!library.optimization.doNotLogLastActions) {
                                            let addToLastActions = true;
                                            for (let i = 0; i < arguments.length; i++) {
                                                if (typeof arguments[i] === 'object' && typeof arguments[i].localStore !== 'undefined') {
                                                    // do not save actions with this or component in arguments
                                                    addToLastActions = false;
                                                }
                                            }
                                            // eslint-disable-next-line no-constant-condition
                                            if (addToLastActions && typeof errorHandler !== 'undefined') {
                                                errorHandler.addLastActions(this.cId, fName, arguments);
                                            }
                                        }
                                    }
                                    if (library.optimization.log.methods) {
                                        let start = performance.now();
                                        // eslint-disable-next-line consistent-return
                                        return this['method' + library.capitalizeFirstLetter(fName)].apply(null, arguments).catch(
                                            (tmpError) => {
                                                this.errorCaptured(tmpError);
                                            }
                                        ).finally(() => {
                                            // eslint-disable-next-line no-console
                                            console.log(this.cId, fName, performance.now() - start);
                                        });
                                    }
                                    // eslint-disable-next-line consistent-return
                                    return this['method' + library.capitalizeFirstLetter(fName)].apply(null, arguments).catch(
                                        (tmpError) => {
                                            this.errorCaptured(tmpError);
                                        }
                                    );
                                };
                            }(i));
                        } else {
                            component.methods[i] = (function (fName) {
                                return function () {
                                    if (typeof this !== 'undefined') {
                                        if (typeof freezedFunctions[this.cId + '_' + fName] === 'undefined') freezedFunctions[this.cId + '_' + fName] = 0;
                                        freezedFunctions[this.cId + '_' + fName]++;
                                        if (freezedFunctions[this.cId + '_' + fName] > 1000 && !this.freezed && excludeFunctionsFromFreeze.indexOf(fName) === -1) {
                                            this.freezed = true;
                                            setTimeout(() => {
                                                errorHandler.handle("Browser freeze!!! Caused by '" + this.cId + "' component in '" + fName + "' function.");
                                            }, 1000);
                                            return;
                                        }
                                        setTimeout(() => {
                                            delete freezedFunctions[this.cId + '_' + fName];
                                        }, 1000);
                                        if (!library.optimization.doNotLogLastActions) {
                                            let addToLastActions = true;
                                            for (let i = 0; i < arguments.length; i++) {
                                                if (arguments[i] && typeof arguments[i] === 'object' && typeof arguments[i].localStore !== 'undefined') {
                                                    // do not save actions with this or component in arguments
                                                    addToLastActions = false;
                                                }
                                            }
                                            // eslint-disable-next-line no-constant-condition
                                            if (addToLastActions && typeof errorHandler !== 'undefined') {
                                                errorHandler.addLastActions(this.cId, fName, arguments);
                                            }
                                        }
                                    }
                                    if (library.optimization.log.methods) {
                                        let start = performance.now();
                                        let result = this['method' + library.capitalizeFirstLetter(fName)].apply(null, arguments);
                                        // eslint-disable-next-line no-console
                                        console.log(this.cId, fName, performance.now() - start);
                                        // eslint-disable-next-line consistent-return
                                        return result;
                                    }
                                    // eslint-disable-next-line consistent-return
                                    return this['method' + library.capitalizeFirstLetter(fName)].apply(null, arguments);
                                };
                            }(i));
                        }
                    }
                    data.vuex = vuex;
                    component.data = function () {
                        return {
                            vuex: vuex
                        };
                    };
                }
            };

            if (typeof component !== 'undefined') {
                // TODO Convert controls on component configs to key/value instead of array
                // This is necessary because controls on configs are an array
                if (settings.controls) {
                    component.controlConfigs = {};
                    Object.values(settings.controls).forEach((control) => {
                        component.controlConfigs[control.name] = control;
                    });
                }
                // Get Component Configuration
                if (typeof component.name !== 'undefined') {
                    getComponentConfig();
                }
                cId = component.name;

                // Create Mixins
                if (typeof component.mixins === 'undefined') component.mixins = [];

                // Create Data
                let data = typeof component.data === 'function' ? component.data() : {};

                // Create Props
                let props = typeof component.props !== 'undefined' ? component.props : {};
                if (typeof props['testMode'] === 'undefined') {
                    props['testMode'] = {
                        type: Boolean,
                        default: false
                    };
                }

                setProps(props, data);
            }
            if (cId) {
                componentStore.mutateComponent(cId, settings, false, false, false, component, path);
            }
        },
        async createComponent({ state, commit }, payload) {
            let [settings, createOptions] = Object.values(payload);
            if (library.objIsEmpty(settings)) return false;
            let response = await sDs.post("sds_createComponentConfig", settings, createOptions);
            if (response.status === 'success') {
                library.showToast('Component created', { timeout: 10 });
                let storeKey = library.getSessionStorage('devDashEnv') + '/sds_getComponentConfig/' + settings.cId;
                let componentListKey = library.getSessionStorage('devDashEnv') + '/sds_getComponentList';
                sDs.updateRequestModifiedList([storeKey, componentListKey], true);
            }
            if (Array.isArray(response.error)) {
                response.error.unshift('Warning:');
                library.showToast(response.error.join('<br/>'), { timeout: 10, type: 'warning' });
            }
            return response;
        },
        async saveComponent({ state, commit }, payload) {
            let [settings, localOnly, cId, previewId, hideSuccessToast, successMsg] = Object.values(payload);
            delete settings['configLoaded'];
            delete settings['loaded'];
            delete settings['statistic'];
            if (library.objIsEmpty(settings)) return false;
            let response = {};
            if (!localOnly) {
                let settingsToSave = library.cloneObj(settings);
                delete settingsToSave.unsaved;
                delete settingsToSave.isNew;
                delete settingsToSave.fromDb;
                response = await sDs.post("sds_updateComponentConfig", settingsToSave, true);
                if (response.status === 'success') {
                    let storeKey = library.getSessionStorage('devDashEnv') + '/sds_getComponentConfig/' + cId;
                    let componentListKey = library.getSessionStorage('devDashEnv') + '/sds_getComponentList';
                    if (projectAllowsCaching) {
                        library.cachedTransaction('put', storeKey, JSON.stringify(settingsToSave));
                        sDs.updateRequestModifiedList([storeKey, componentListKey], true);
                    }
                    // state.components[settings.cId].loaded = false;
                    if (settings.unsaved) settings.isNew = true;
                    delete settings.unsaved;
                    settings.fromDb = true;
                    if (settings.prevCId && response.data !== settings.prevCId) {
                        delete state.components[settings.prevCId];
                    }
                    state.components[settings.cId] = settings;
                    state.components[settings.cId].loaded = true;
                    if (!settings['prevCId']) componentStore.mutateComponent(cId, settings, previewId);
                    if (!hideSuccessToast) library.showToast(successMsg || 'Component saved', { timeout: 10 });
                }
                if (Array.isArray(response.error)) {
                    response.error.unshift('Warning:');
                    library.showToast(response.error.join('<br/>'), { timeout: 10, type: 'warning' });
                }
            } else {
                settings.unsaved = true;
                state.components[settings.cId] = settings;
                state.components[settings.cId].loaded = true;
                delete settings['prevCId'];
                componentStore.mutateComponent(cId, settings, previewId);
                response.status = 'success';
                response.data = cId;
                if (!hideSuccessToast) library.showToast(successMsg || 'PREVIEW ONLY component config updated', { timeout: 10 });
            }
            // eslint-disable-next-line consistent-return
            return response && response.data ? response.data : response;
        },
        async newComponent({ state, commit, dispatch }, payload) {
            let [cType, cId, cTitle, andSave, isPublic] = Object.values(payload);
            if (typeof state.components[cType] === 'undefined') return undefined;
            let currentCoordinatorUser = uStore().userData && uStore().userData.coordinator;
            if (!currentCoordinatorUser) return undefined;
            if (!state.components[cType].loaded) {
                await componentStore.getConfig(cType, false, true);
            }
            let newComponent = library.cloneObj(state.components[cType]);
            newComponent.configLocked = false;
            newComponent.lockedConfig = false;
            newComponent.isNew = true;
            newComponent.isPublic = isPublic || false;
            // Set default values
            if (newComponent && state.componentsConfig[cType] && state.componentsConfig[cType].editorSettings) {
                for (let i in state.componentsConfig[cType].editorSettings) {
                    if (i === 'length') continue;
                    if (typeof state.componentsConfig[cType].editorSettings[i].default !== 'undefined') {
                        newComponent[i] = library.cloneObj(state.componentsConfig[cType].editorSettings[i].default);
                    }
                }
            }
            if (cTitle) newComponent.cTitle = cTitle;
            // Set the cId for new component
            if (!cId) cId = newComponent.cType + '_' + library.getUniqueID();
            // Make sure the cId is not a duplicate
            let existingComponent = Object.values(state.components).find((existing) => existing.cId === cId);
            if (existingComponent) {
                cId = newComponent.cId + '_' + library.getUniqueID();
                library.showToast('The component ID you have entered is already in use.  Component IDs must be unique. We have changed the ID to: ' + cId);
            }
            newComponent.cId = cId;
            if (andSave) {
                await dispatch('createComponent', [newComponent]);
            } else {
                newComponent.unsaved = true;
            }
            let createDate = moment().format();
            // Note additionalData set here is for local data only, and may change after refreshing componentList or getting the componentConfig
            locals.setAdditionalData(newComponent, {
                cCreatedBy: currentCoordinatorUser.CLIENT_EMAIL,
                cCreatedDate: createDate,
                cLastModify: createDate,
                cModifiedBy: currentCoordinatorUser.CLIENT_EMAIL
            });
            componentStore.mutateComponent(cId, newComponent);
            return cId;
        },
        async copyComponent({ state, commit }, payload) {
            let [cId, cIdNew, cTitleNew, isPublicNew] = Object.values(payload);
            let iConfig = await componentStore.getConfig(cId, false, true);
            let response = {};
            if (!iConfig.fromDb) {
                iConfig.configLocked = false;
                iConfig.lockedConfig = false;
                response = await componentStore.saveComponent(iConfig);
                if (response.status === 'success') {
                    await componentStore.getConfig(response.data);
                }
            }
            response = await sDs.post("sds_copyComponentConfig", cId, cIdNew, cTitleNew, isPublicNew);
            let settings = library.cloneObj(state.components[cId]);
            if (response.status === 'success') {
                library.showToast('Component copied', { timeout: 10 });
                cId = response.data;
                settings.cId = cId;
                settings.isNew = true;
                settings.lockedConfig = false;
                settings.cTitle = cTitleNew;
                settings.isPublic = isPublicNew;
                componentStore.mutateComponent(cId, settings);
            }
            if (Array.isArray(response.error)) {
                response.error.unshift('Warning:');
                library.showToast(response.error.join('<br/>'), { timeout: 10, type: 'warning' });
                cId = false;
            }
            return cId;
        },
        async deleteComponent({ state, commit }, payload) {
            let [cId] = Object.values(payload);
            let deleted = false;
            let iConfig = await componentStore.getConfig(cId);
            if (iConfig.lockedConfig) {
                library.showToast('This component cannot be deleted because it is still locked or partially locked.  To delete, you must fully unlock the component.', { timeout: 15, type: 'warning' });
                return deleted;
            }
            let response = await sDs.post("sds_deleteComponentConfig", cId);
            if (response.status === 'success') {
                let tmpComponents = state.components;
                delete tmpComponents[cId];
                await componentStore.mutate('components', tmpComponents);
                deleted = true;
                library.showToast('Component deleted', { timeout: 10 });
            }
            if (Array.isArray(response.error)) {
                response.error.unshift('Warning:');
                library.showToast(response.error.join('<br/>'), { timeout: 10, type: 'warning' });
            }
            let cIdReqKey = 'sds_getComponentConfig_' + cId;
            let componentListKey = library.getSessionStorage('devDashEnv') + '/sds_getComponentList';
            sDs.updateRequestModifiedList([cIdReqKey, componentListKey], true);
            return deleted;
        },
        async exportComponent({ state, commit }, payload) {
            let [cId] = Object.values(payload);
            if (state.components && state.components[cId]) {
                if (!state.components[cId].loaded) {
                    await componentStore.getConfig(cId, false, true);
                }
                let tmpConfig = library.cloneObj(state.components[cId]);
                let propsForDelete = [
                    'loaded',
                    'configLoaded',
                    'statistic',
                    'cTemplate',
                    'modifiedInstances',
                    'isGlobal',
                    'additionalData',
                    'fromDb',
                    'fullId',
                    'cCreatedBy',
                    'cCreatedDate',
                    'cLastModify',
                    'cLastModifyUnixDay',
                    'dateLastModified',
                    'daysSinceLastModify',
                    'cModifiedBy',
                ];
                for (let i in propsForDelete) {
                    if (i === 'length') continue;
                    if (typeof tmpConfig[propsForDelete[i]] !== 'undefined') {
                        delete tmpConfig[propsForDelete[i]];
                    }
                }
                // Open a dialog with an option to cut/paste, or use export to file
                library.exportFile(state.components[cId].cTitle + '.json', JSON.stringify(tmpConfig, false, '    '));
            }
        },
        async importComponent({ state, commit }, payload) {
            let [cId] = Object.values(payload);
            let tmpConfig = "";
            if (cId) {
                tmpConfig = await componentStore.getConfig(cId, false, true);
                tmpConfig = library.cloneObj(tmpConfig);
            }
            let validConfig = false;
            let importSelectedOption = 0;
            let importOptions = [];
            if (tmpConfig && tmpConfig.cId) {
                importOptions = [
                    { "label": "Save as new", "value": 0 },
                    { "label": "Rewrite selected" + (tmpConfig.cId ? ' ( ' + tmpConfig.cId + ' ) ' : ''), "value": 1 }
                ];
            }
            let importTemplate = {
                name: "component-import-template_" + library.getUniqueID(),
                template: `<div id="componentImportTemplate">
                    <p>Paste JSON or upload a file to import.</p>
                    <control name="IMPORT_FILE_UPLOAD" :prop-config="importFileControl" :valueFiles="importFileValues" @update:valueFiles="importFileValues = $event; fileChanged()"></control>
                    <control name="IMPORT_JSON" :prop-config="importJsonControl" :value="importingConfig" @update:value="importingConfig = $event; configChanged()" style="margin-top: 10px;"></control>
                    <p v-show="isCurrentConfig"><strong>No config changes found. Try importing a file or pasting a config.</strong></p>
                    <control v-if="importOptions && importOptions.length" name="IMPORT_UPLOAD_OPTION" :items="importOptions" :prop-config="importOptionControl" :value="importSelectedOption" @update:value="importSelectedOption = $event; optionChanged()"></control>
                </div>`,
                data() {
                    return {
                        importOptionControl: {
                            "name": "IMPORT_UPLOAD_OPTION",
                            "ref": "IMPORT_UPLOAD_OPTION",
                            "type": "RADIO_BUTTON_LIST",
                            "default": 0
                        },
                        importFileControl: {
                            "name": "IMPORT_FILE_UPLOAD",
                            "ref": "IMPORT_FILE_UPLOAD",
                            "type": "upload",
                            "allowedExtensions": ".json",
                            "allowedExtensionsText": ".json",
                            "showAsDefaultInput": false,
                            "entityName": "Component",
                            "multiple": false,
                            "showRemoveConfirmation": false,
                            "showExtensionsText": true,
                        },
                        importJsonControl: {
                            "name": "IMPORT_JSON",
                            "label": "Component Config",
                            "ref": "IMPORT_JSON",
                            "type": "text",
                            "rows": 15
                        },
                        cId: cId,
                        tmpConfig: tmpConfig,
                        validJson: false,
                        importFileValues: null,
                        isCurrentConfig: !!tmpConfig,
                        importingConfig: JSON.stringify(tmpConfig),
                        importSelectedOption: 0,
                        importOptions: importOptions
                    };
                },
                methods: {
                    optionChanged() {
                        importSelectedOption = this.importSelectedOption;
                    },
                    configChanged() {
                        this.validJson = library.isJson(this.importingConfig);
                        validConfig = this.validJson ? this.importingConfig : false;
                        this.isCurrentConfig = false;
                    },
                    fileChanged() {
                        if (this.importFileValues && this.importFileValues[0] && this.importFileValues[0].base64) {
                            let base64 = library.cloneObj(this.importFileValues[0].base64);
                            base64 = base64.replace('data:application/json;base64,', '');
                            let importingConfig = library.base64ToFileContent(base64);
                            if (importingConfig) {
                                this.importingConfig = importingConfig;
                                this.validJson = library.isJson(importingConfig);
                                validConfig = this.validJson ? this.importingConfig : false;
                                this.isCurrentConfig = false;
                            }
                        } else {
                            this.importingConfig = "";
                            this.validJson = false;
                            validConfig = false;
                        }
                    }
                },
                components: {
                    control: defineAsyncComponent(() => import('@common/control/control.vue'))
                }
            };
            let funcOnSuccess = async () => {
                if (validConfig) {
                    let cConfig = JSON.parse(validConfig);
                    for (let i in cConfig) {
                        if (i === 'length') continue;
                        tmpConfig[i] = library.cloneObj(cConfig[i]);
                    }
                    if (importSelectedOption === 0) {
                        tmpConfig['cId'] = tmpConfig['cType'] + '_' + library.getUniqueID();
                    } else {
                        tmpConfig['cId'] = cId;
                    }
                    library.showLoadingIndicator('componentImportTemplate');
                    let componentID = await componentStore.saveComponent(tmpConfig);
                    library.hideLoadingIndicator('componentImportTemplate');
                    return componentID;
                }
                library.showDialog('JSON is not valid ', false, false, { title: 'Error', showAsAlert: true });
                return false;
            };
            return library.showDialog(importTemplate, funcOnSuccess, false, { title: 'Import Component', doNotCloseOnFuncFailure: true });
        },
        async setTestData({ state, commit }, payload) {
            let [key, cId, testData] = Object.values(payload);
            await sDs.post("sds_setTestData", key, cId, testData);
            let testDataKey = library.getSessionStorage('devDashEnv') + '/sds_getTestData';
            sDs.updateRequestModifiedList([testDataKey]);
        },
        async deleteTestData({ state, commit }, payload) {
            let [key] = Object.values(payload);
            await sDs.post("sds_deleteTestData", key);
            let testDataKey = library.getSessionStorage('devDashEnv') + '/sds_getTestData';
            sDs.updateRequestModifiedList([testDataKey]);
        }
    }
};

store.registerModule("componentStore", componentStore);
export { componentStore, componentGlobalConfig, componentGlobalMixins };
